// src/components/chat/ChatView.js
// Vue de messagerie Client <-> Marchand (liste des conversations + fil)
// Maxula

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FiSend, FiMessageCircle, FiShield, FiArrowLeft, FiExternalLink } from 'react-icons/fi';
import { chatAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { useSettings } from '../../context/SettingsContext';

// Couleurs de repli si l'admin n'a pas défini de couleur principale.
const DEFAULT_BRAND = '#E11D79';
const DEFAULT_BRAND_HOVER = '#D0176A';
const DEFAULT_AVATAR_TINT = '#FCE7F1';

// --- Dérivation de la palette depuis la couleur admin (mêmes maths que SettingsContext) ---
const _clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
const _hexToRgb = (hex) => {
  const h = String(hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full || 'e11d79', 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const _rgbToHex = ({ r, g, b }) => '#' + [r, g, b].map((v) => _clamp(v).toString(16).padStart(2, '0')).join('');
const _mix = (h1, h2, w) => {
  const a = _hexToRgb(h1), b = _hexToRgb(h2);
  return _rgbToHex({ r: a.r + (b.r - a.r) * w, g: a.g + (b.g - a.g) * w, b: a.b + (b.b - a.b) * w });
};
const _lighten = (h, w) => _mix(h, '#ffffff', w);
const _darken = (h, w) => _mix(h, '#000000', w);
const _rgba = (hex, a) => { const { r, g, b } = _hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };

// Construit toutes les teintes du chat à partir de la couleur principale admin.
const buildPalette = (primary, secondary) => {
  const brand = primary || DEFAULT_BRAND;
  const brandHover = secondary || _darken(brand, 0.12);
  return {
    brand,
    brandHover,
    tintAvatar: _lighten(brand, 0.90), // ex-#FCE7F1
    tintActive: _lighten(brand, 0.93), // ex-#FDF2F8 (fond conversation active)
    threadBg: _lighten(brand, 0.97),   // ex-#FAF7F9 (fond du fil)
    tintBorder: _lighten(brand, 0.80), // ex-#F3C9E0 (bordure pill)
    dot: _rgba(brand, 0.06),           // motif pointillé du fil
    focusRing: _rgba(brand, 0.18)      // halo de focus de l'input
  };
};

// Porte #5 (anti-désintermédiation) : masquage léger côté client pour l'écho
// optimiste (le serveur reste la source de vérité et masque aussi à l'envoi).
const maskContact = (input) => {
  let t = String(input || '');
  // 1) E-mails classiques
  t = t.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[contact masqué]');
  // 2) E-mails obfusqués (nom at domaine dot com)
  t = t.replace(/[a-zA-Z0-9._%+-]+\s*[[({]?\s*(?:@|at|arobase)\s*[\])}]?\s*[a-zA-Z0-9.-]+\s*[[({]?\s*(?:\.|dot|point)\s*[\])}]?\s*[a-zA-Z]{2,}/gi, '[contact masqué]');
  // 3) Liens & domaines réseaux
  t = t.replace(/\b(?:https?:\/\/)?(?:www\.)?(?:wa\.me|t\.me|api\.whatsapp\.com|m\.me|whatsapp\.com|telegram\.me|instagram\.com|facebook\.com|fb\.com|snapchat\.com|tiktok\.com)\/?\S*/gi, '[lien masqué]');
  // 4) Handle après mot-clé réseau (si identifiant : @, +, ou contient un chiffre)
  t = t.replace(/\b(?:whats?app|wtsp|watsap|telegram|telegramme|insta(?:gram)?|snap(?:chat)?|signal|viber|messenger|imo)\b[\s:=.-]*((?:@|\+)[\w.-]{2,}|[\w.-]*\d[\w.-]*)/gi, '[contact masqué]');
  // 5) Téléphones — séparateurs symboliques variés, même répétés (* ** // _ # | = : …)
  const SEP = '[\\d\\s.\\-/*_~#|=+():]';
  t = t.replace(new RegExp('[+(]?\\d' + SEP + '{4,}\\d', 'g'), (m) => {
    const d = m.replace(/\D/g, '');
    return d.length >= 9 && d.length <= 20 ? '[contact masqué]' : m;
  });
  // 6) Préfixes internationaux explicites
  t = t.replace(new RegExp('(?:\\+|00)\\d' + SEP + '{3,}\\d', 'g'), (m) => {
    const d = m.replace(/\D/g, '');
    return d.length >= 7 ? '[contact masqué]' : m;
  });
  return t;
};

// --- présentation (pur affichage) ---
const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase();
};
const dayKey = (d) => new Date(d).toDateString();
const dayLabel = (d) => {
  const date = new Date(d);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === yest.toDateString()) return 'Hier';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};
const hhmm = (d) => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const isRecent = (d) => d && (Date.now() - new Date(d).getTime()) < 5 * 60 * 1000; // activité réelle < 5 min

// Avatar (image réelle si dispo, sinon initiales), avec pastille d'activité optionnelle
const Avatar = ({ name, src, size = 40, active = false, tint = DEFAULT_AVATAR_TINT, fg = DEFAULT_BRAND_HOVER }) => {
  const [failed, setFailed] = useState(false);
  // Réinitialise l'état d'erreur quand la source change (header réutilisé entre fils).
  useEffect(() => { setFailed(false); }, [src]);
  const showImg = src && !failed;
  return (
    <span className="relative shrink-0 inline-flex" style={{ width: size, height: size }}>
      {showImg ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="w-full h-full rounded-full object-cover bg-gray-100"
        />
      ) : (
        <span className="w-full h-full rounded-full flex items-center justify-center font-semibold"
          style={{ backgroundColor: tint, color: fg, fontSize: size * 0.36 }}>
          {initials(name)}
        </span>
      )}
      {active && (
        <span title="Actif récemment"
          className="absolute bottom-0 right-0 block w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white" />
      )}
    </span>
  );
};

const ChatView = ({ as = 'customer' }) => {
  const { settings } = useSettings();
  const palette = useMemo(
    () => buildPalette(settings?.primaryColor, settings?.secondaryColor),
    [settings?.primaryColor, settings?.secondaryColor]
  );
  const BRAND = palette.brand;
  const BRAND_HOVER = palette.brandHover;

  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [role, setRole] = useState(as);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const location = useLocation();

  const otherName = (c) =>
    as === 'merchant'
      ? `${c.customerId?.firstName || ''} ${c.customerId?.lastName || ''}`.trim() || 'Client'
      : c.merchantId?.businessName || 'Marchand';
  const otherAvatar = (c) =>
    normalizeImagePath(as === 'merchant' ? c.customerId?.avatar : c.merchantId?.logo);

  const loadConvs = useCallback(async () => {
    try {
      const res = await chatAPI.list(as === 'merchant' ? 'merchant' : undefined);
      const list = res.data?.data || [];
      setConvs(list);
      setRole(res.data?.role || as);
      return list;
    } catch (e) { return []; }
  }, [as]);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await chatAPI.messages(id);
      setMessages(res.data?.data?.messages || []);
      setRole(res.data?.data?.role || as);
      // La conversation est marquée lue côté serveur (merchantUnread -> 0) :
      // on demande au layout de rafraîchir le badge "Messages" tout de suite.
      try { window.dispatchEvent(new Event('badges:refresh')); } catch (e) {}
    } catch (e) {}
  }, [as]);

  // init : charge convs, ouvre celle passée en query (?c=ID) ou la première
  useEffect(() => {
    (async () => {
      const list = await loadConvs();
      const params = new URLSearchParams(location.search);
      const wanted = params.get('c');
      const first = wanted && list.find((x) => String(x._id) === wanted) ? wanted : (list[0]?._id || null);
      if (first) { setActiveId(first); loadMessages(first); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // polling toutes les 8s (messages + liste)
  useEffect(() => {
    const t = setInterval(() => {
      loadConvs();
      if (activeId) loadMessages(activeId);
    }, 8000);
    return () => clearInterval(t);
  }, [activeId, loadConvs, loadMessages]);

  // Défile uniquement le conteneur des messages vers le bas (jamais la fenêtre).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, activeId]);

  const openConv = (id) => { setActiveId(id); loadMessages(id); };

  const send = async () => {
    if (!text.trim() || !activeId) return;
    const t = text.trim();
    setText('');
    setSending(true);
    // optimiste
    setMessages((m) => [...m, { _id: 'tmp' + Date.now(), senderRole: role, text: maskContact(t), createdAt: new Date().toISOString(), _tmp: true }]);
    try {
      await chatAPI.send(activeId, t);
      await loadMessages(activeId);
      loadConvs();
    } catch (e) {
      setMessages((m) => m.filter((x) => !x._tmp));
      setText(t);
    } finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: BRAND }} /></div>;

  const active = convs.find((c) => String(c._id) === String(activeId));

  // Fond texturé léger (style messagerie)
  const threadBg = {
    backgroundColor: palette.threadBg,
    backgroundImage: `radial-gradient(${palette.dot} 1px, transparent 1px)`,
    backgroundSize: '18px 18px',
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden grid grid-cols-1 md:grid-cols-3 h-[calc(100vh-160px)] min-h-[460px] max-h-[860px] shadow-sm">
      {/* Liste */}
      <div className={`border-r border-gray-100 overflow-y-auto min-h-0 bg-gray-50/40 ${active ? 'hidden md:block' : ''}`}>
        <div className="px-4 py-3 border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur z-10 flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Conversations</span>
          {convs.length > 0 && (
            <span className="text-xs text-white rounded-full px-2 py-0.5" style={{ backgroundColor: BRAND }}>{convs.length}</span>
          )}
        </div>
        {convs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm flex flex-col items-center gap-2">
            <FiMessageCircle size={28} className="text-gray-300" />
            Aucune conversation.
          </div>
        ) : convs.map((c) => {
          const unread = as === 'merchant' ? c.merchantUnread : c.customerUnread;
          const isActive = String(c._id) === String(activeId);
          return (
            <button key={c._id} onClick={() => openConv(c._id)}
              className={`w-full text-left px-3 py-3 border-b border-gray-100 flex gap-3 transition-colors border-l-[3px] ${isActive ? '' : 'border-l-transparent hover:bg-gray-100/70'}`}
              style={isActive ? { backgroundColor: palette.tintActive, borderLeftColor: BRAND } : undefined}>
              <Avatar name={otherName(c)} src={otherAvatar(c)} size={44} active={isRecent(c.lastMessageAt)} tint={palette.tintAvatar} fg={palette.brandHover} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className={`truncate ${unread > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>{otherName(c)}</span>
                  {c.lastMessageAt && <span className="text-[10px] text-gray-400 shrink-0">{hhmm(c.lastMessageAt)}</span>}
                </span>
                <span className="block text-xs text-gray-500 truncate">{c.dealTitle || 'Annonce'}</span>
                <span className="flex items-center justify-between gap-2 mt-0.5">
                  <span className={`block text-xs truncate ${unread > 0 ? 'text-gray-700' : 'text-gray-400'}`}>{c.lastMessage || '—'}</span>
                  {unread > 0 && (
                    <span className="shrink-0 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" style={{ backgroundColor: BRAND }}>{unread}</span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Fil */}
      <div className="md:col-span-2 flex flex-col min-h-0">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 px-6 text-center" style={threadBg}>
            <span className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mb-3">
              <FiMessageCircle size={30} style={{ color: BRAND }} />
            </span>
            <p className="font-medium text-gray-600">Sélectionnez une conversation</p>
            <p className="text-xs text-gray-400 mt-1">Choisissez un client dans la liste pour afficher les messages.</p>
          </div>
        ) : (
          <>
            {/* En-tête conversation (plus haut, avec lien vers l'annonce) */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 bg-white">
              <button onClick={() => setActiveId(null)} className="md:hidden text-gray-500 -ml-1 p-1" aria-label="Retour">
                <FiArrowLeft size={18} />
              </button>
              <Avatar name={otherName(active)} src={otherAvatar(active)} size={42} active={isRecent(active.lastMessageAt)} tint={palette.tintAvatar} fg={palette.brandHover} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 truncate leading-tight">{otherName(active)}</div>
                <div className="text-xs text-gray-400 truncate">
                  {isRecent(active.lastMessageAt) ? <span className="text-emerald-600">Actif récemment</span> : (active.dealTitle || 'Annonce')}
                </div>
              </div>
              {active.dealId && (
                <Link to={`/deals/${active.dealId}`}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors"
                  style={{ color: BRAND_HOVER, borderColor: palette.tintBorder }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = palette.tintActive)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
                  <FiExternalLink size={13} /> Voir l'annonce
                </Link>
              )}
            </div>

            {/* Fil texturé + regroupement par expéditeur */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 min-h-0" style={threadBg}>
              <div className="min-h-full flex flex-col justify-end">
              {messages.map((m, i) => {
                const mine = m.senderRole === role;
                const prev = messages[i - 1];
                const next = messages[i + 1];
                const sameDay = prev && dayKey(prev.createdAt) === dayKey(m.createdAt);
                const samePrev = sameDay && prev.senderRole === m.senderRole;
                const sameNext = next && dayKey(next.createdAt) === dayKey(m.createdAt) && next.senderRole === m.senderRole;
                const showSep = !sameDay;
                const corners = mine
                  ? `rounded-2xl ${samePrev ? 'rounded-tr-md' : ''} ${sameNext ? 'rounded-br-md' : 'rounded-br-sm'}`
                  : `rounded-2xl ${samePrev ? 'rounded-tl-md' : ''} ${sameNext ? 'rounded-bl-md' : 'rounded-bl-sm'}`;
                return (
                  <React.Fragment key={m._id}>
                    {showSep && (
                      <div className="flex justify-center my-3">
                        <span className="text-[11px] text-gray-500 bg-white/80 shadow-sm rounded-full px-3 py-0.5">{dayLabel(m.createdAt)}</span>
                      </div>
                    )}
                    <div className={`flex ${mine ? 'justify-end' : 'justify-start'} ${samePrev ? 'mt-0.5' : 'mt-3'}`}>
                      <div className={`max-w-[75%] px-3.5 py-2 text-sm shadow-sm ${corners} ${mine ? 'text-white' : 'bg-white border border-gray-200 text-gray-800'}`}
                        style={mine ? { backgroundColor: BRAND } : undefined}>
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        {!sameNext && (
                          <div className={`text-[10px] mt-1 text-right ${mine ? 'text-white/70' : 'text-gray-400'}`}>{hhmm(m.createdAt)}</div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
              </div>
            </div>

            <div className="px-3 pt-2 flex items-center gap-1.5 text-[11px] text-gray-400 bg-white">
              <FiShield size={12} className="shrink-0" />
              <span>Pour votre sécurité, les numéros et emails sont masqués. Restez sur Maxula pour rester protégé.</span>
            </div>
            <div className="p-3 border-t border-gray-100 flex items-center gap-2 bg-white">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Écrivez votre message…"
                className="flex-1 border border-gray-200 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2"
                style={{ outlineColor: BRAND }}
                onFocus={(e) => { e.target.style.boxShadow = `0 0 0 3px ${palette.focusRing}`; e.target.style.borderColor = BRAND; }}
                onBlur={(e) => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = '#E5E7EB'; }}
              />
              <button onClick={send} disabled={sending || !text.trim()}
                className="w-11 h-11 rounded-full text-white flex items-center justify-center active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                style={{ backgroundColor: BRAND }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = BRAND_HOVER; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = BRAND; }}>
                <FiSend size={17} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatView;
